/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN STUDENT FEE PLAN SELECTORS
 * Role: Admin
 * Scope: Memoized state selectors for student fee plans, audit history,
 *        loading states, and error handling.
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { createSelector } from "@reduxjs/toolkit";

/**
 * Base selector to isolate the student fee plan slice safely, 
 * accommodating multiple Redux tree mounting patterns.
 * 
 * @param {Object} state - The root Redux state
 * @returns {Object} The student fee plan state slice
 */
export const selectStudentFeePlanState = (state) =>
  state?.admin?.studentFeePlan || state?.studentFeePlan || {};

/**
 * Selector: Retrieves the current active fee plan configuration.
 */
export const selectActiveFeePlan = createSelector(
  [selectStudentFeePlanState],
  (feePlanState) => feePlanState.activePlan || null
);

/**
 * Selector: Retrieves the chronological audit history array of fee modifications.
 */
export const selectFeePlanHistory = createSelector(
  [selectStudentFeePlanState],
  (feePlanState) => feePlanState.history || []
);

/**
 * Selector: Retrieves the current asynchronous loading status indicator.
 */
export const selectFeePlanLoading = createSelector(
  [selectStudentFeePlanState],
  (feePlanState) => feePlanState.loading || false
);

/**
 * Selector: Retrieves the current error message or state payload if any action fails.
 */
export const selectFeePlanError = createSelector(
  [selectStudentFeePlanState],
  (feePlanState) => feePlanState.error || null
);